import React, { useEffect, useRef, useState } from 'react'
import { useSize } from 'components/Charts_Assets'
import { scaleLinear, ScaleLinear } from 'd3-scale'

import { BFS_FREQUENCY_AGE_Type } from '../MainComponent/bfs_frequenzy_agegroup'
import { Marker } from 'components/Marker'
import { render } from 'react-dom'
import { Size } from 'components/Charts_Assets/useSize'
import styled from 'styled-components'
const isMobile = window.innerWidth < 712
const n = (num: number, precision: number = 1) =>
  (+num.toFixed(precision)).toLocaleString('de')
const BARCOLORS: { [key: string]: string[] } = {
  m: [
    'var(--purple-dark-1)',
    'var(--purple-bright-1)',
    'var(--blue-main)',
    'var(--blue-bright-2)',
    'var(--gray-4)',
  ],
  f: [
    'var(--purple-dark-1)',
    'var(--purple-bright-1)',
    'var(--blue-main)',
    'var(--blue-bright-2)',
    'var(--gray-4)',
  ],
}
type ChartProps = {
  data: BFS_FREQUENCY_AGE_Type[]
  props?: string[]
  className: string
  height: number
  marked?: { label: string; data: UserInputTypes; show: boolean }
  gender?: string
  rowLabel: (e: BFS_FREQUENCY_AGE_Type) => string
  groupLabels: string[]
}

export const StackedBars: React.FC<ChartProps> = ({
  data,
  props = ['daily', 'threetosix', 'onetotwo', 'lesthanone', 'abstinent'],
  height = 40,
  className,
  marked,
  gender = 'n',
  rowLabel,
  groupLabels,
}) => {
  const [tooltipContent, setTooltipContent] = useState<
    | {
        label: string
        value: number
        pos: { x: number; y: number }
        group: number
      }
    | undefined
  >()
  const [container, size] = useSize()
  const markerRef = useRef<SVGGElement>(null)

  const svg = useRef<SVGSVGElement>(null)

  const width = size.width
  const gutterTop = 0
  const gutterRight = 1
  const gutterLeft = 1
  const gutterBottom = 0
  const plotArea = {
    top: gutterTop,
    right: width - gutterRight,
    left: gutterLeft,
    bottom: height - gutterBottom,
  }

  const xAxisScale = React.useMemo(
    () =>
      scaleLinear<number, number>()
        .domain([0, 100])
        .range([plotArea.left, plotArea.right - (isMobile ? 98 : 128)]),
    [size]
  )

  const barSpace = 6
  const barHeight = (plotArea.bottom - plotArea.top) / data.length - barSpace

  return (
    <StyledStackedBars className={'stackedbars ' + className}>
      <div
        ref={container}
        style={{ position: 'relative', width: '100%', height: height }}
      >
        <svg
          ref={svg}
          height={height}
          width={'50%'}
          style={{ backgroundColor: 'transparent' }}
          onMouseOut={() => setTooltipContent(undefined)}
          onClick={(e) => {
            let target=e.target  as any
            let {dataset} = target
            if (dataset)
              setTooltipContent({
                label: dataset.label,
                value: +dataset.value,
                pos: { x: +dataset.posX, y: dataset.posY },
                group: dataset.group,
              })
          }}
        >
          <defs>
            <filter
              id='rounded-corners'
              x='-10%'
              width='120%'
              y='-10%'
              height='120%'
            >
              <feFlood floodColor='#fff' />
              <feComponentTransfer>
                <feFuncA type='table' tableValues='0 0 0 1' />
              </feComponentTransfer>
              <feComposite operator='over' in='SourceGraphic' />
            </filter>
          </defs>
          {data.map((m, i) => (
            <BarRow
              key={'barrow' + i}
              gender={gender}
              xScale={xAxisScale}
              props={props}
              rowLabel={rowLabel}
              data={m}
              mark={
                marked
                  ? marked.data.age === m.agegroup
                    ? { ...marked, target: markerRef.current }
                    : undefined
                  : undefined
              }
              x={isMobile ? 90 : 120}
              y={plotArea.top + i * (barHeight + barSpace)}
              width={size.width}
              height={barHeight}
              size={size}
            />
          ))}
          <g ref={markerRef}></g>
          {tooltipContent && (
            <Tooltip
              pos={tooltipContent.pos}
              value={tooltipContent.value}
              group={tooltipContent.group}
              groupLabels={groupLabels}
              fullWidth={size.width}
            />
          )}
        </svg>
      </div>
    </StyledStackedBars>
  )
}

const StyledStackedBars = styled.g`
  // margin-top: 20px;
  font-size: 18px;
  // margin-bottom: 15px;
  svg {
    overflow: visible;
    width: 100%;
    font-family: var(--chart-font-stack);
    font-weight: 200;
    font-size: 15px;
    @media only screen and (max-width: 711px) {
      font-size: 13px;
    }
    text.value {
      font-size: 12px;
    }
  }
`
const StyledTooltip = styled.g`
  pointer-events: none;
  user-select: none;
  -webkit-filter: drop-shadow(0px 0px 3px rgba(0, 0, 0, 0.7));
  filter: drop-shadow(0px 0px 3px rgba(0, 0, 0, 0.7));
  text {
    font-weight: 400;
    .val {
      font-weight: 700;
    }
    &.bg {
      stroke: #f00;
      stroke-width: 40;
      stroke-linejoin: round;
    }
  }
`

const Tooltip = ({
  pos,
  value,
  group,
  groupLabels,
  fullWidth,
}: {
  pos: { x: number; y: number }
  group: number
  value: number
  fullWidth: number
  groupLabels: string[]
}) => {
  if (!+value) return null
  return (
    <StyledTooltip
      className='tooltip'
      transform={`translate(${pos.x + (pos.x < fullWidth * 0.5 ? 20 : -20)},${
        pos.y - 15
      })`}
    >
      <text
        x={10}
        y={20}
        textAnchor={pos.x < fullWidth * 0.5 ? 'start' : 'end'}
        filter='url(#rounded-corners)'
      >
        <tspan className='val'>{n(value)}%</tspan>
        {!isMobile && <tspan dx={8}>{groupLabels[group]}</tspan>}
      </text>
    </StyledTooltip>
  )
}

const BarRow = ({
  data,
  xScale,
  props,
  x,
  y,
  height,
  width,
  mark,
  gender,
  rowLabel,
  size,
}: {
  xScale: ScaleLinear<number, number>
  data: BFS_FREQUENCY_AGE_Type
  props: string[]
  x: number
  y: number
  height: number
  width: number
  gender: string
  size: Size
  mark?: {
    data: { frequency: string; age: string }
    target: SVGGElement | null
    label: string
    show: boolean
  }

  rowLabel: (e: BFS_FREQUENCY_AGE_Type) => string
}) => {
  let curX = 0
  return (
    <g className='barrow' transform={`translate(${x},${y})`}>
      {props.map((prop, i) => {
        let w = Math.ceil(xScale(+data[prop]) || 0)
        curX = Math.round(curX + w)

        if (mark && mark.target && mark.data.frequency === prop && mark.show) {
          let mx = x + Math.round(curX - w) + w / 2
          render(
            [
              <Marker
                text={mark.label}
                x={mx}
                y={y}
                position={
                  mx <= size.width * 0.5
                    ? 'right'
                    : mx > size.width * 0.5
                    ? 'left'
                    : 'middle'
                }
              />,
            ],
            mark.target
          )
        }
        if (w < 0) return null
        return (
          <g key={'stackedrect' + i}>
            <StackedBarsRect
              width={w}
              x={Math.round(curX - w)}
              height={height - 6}
              fill={BARCOLORS[gender][i]}
              data-label={prop}
              data-value={+data[prop]}
              data-pos-x={x + Math.round(curX - w)}
              data-pos-y={y + height / 2 - 3}
              data-group={i}
            />

            {mark &&
              mark.target &&
              mark.data.frequency === prop &&
              mark.show &&
              +data[prop] > 14 && (
                <text
                  className='value'
                  x={Math.round(curX - w) + w / 2}
                  y={height / 2 + 2}
                  fill={i < 3 ? '#fff' : '#202346'}
                  fontWeight='700'
                  textAnchor='middle'
                  style={{ pointerEvents: 'none' }}
                >
                  {n(+data[prop])}%
                </text>
              )}
          </g>
        )
      })}

      <text x={-x} y={height * 0.6}>
        {rowLabel(data)}
      </text>
      <line
        x1={-x}
        x2={width - x}
        y1={height}
        y2={height}
        stroke={"var(--brandblue-bright-5)"}
        strokeWidth={1.5}
      />
    </g>
  )
}
const StackedBarsRect = styled.rect`
  stroke: #fff;
  stroke-width: 0.5;
  &:hover {
    stroke: #000;
  }
`